import { useEffect, useRef, useState } from 'react';

import { api, ApiClientError } from '../../services/api';
import type { SocialAccount, AdPlatform } from '../../types';

const PLATFORM_INFO: Record<AdPlatform, { name: string; icon: string; color: string; fields: { key: string; label: string; placeholder: string; type?: string }[] }> = {
  whatsapp: {
    name: 'WhatsApp Business',
    icon: '📱',
    color: '#25D366',
    fields: [
      { key: 'phoneNumberId', label: 'Phone Number ID', placeholder: '123456789' },
      { key: 'businessAccountId', label: 'Business Account ID', placeholder: '987654321' },
      { key: 'accessToken', label: 'Access Token', placeholder: 'EAAG...', type: 'password' },
    ],
  },
  facebook: {
    name: 'Facebook Page',
    icon: 'f',
    color: '#1877F2',
    fields: [
      { key: 'pageId', label: 'Page ID', placeholder: '123456789' },
      { key: 'pageAccessToken', label: 'Page Access Token', placeholder: 'EAAG...', type: 'password' },
    ],
  },
  instagram: {
    name: 'Instagram Business',
    icon: '📷',
    color: '#E4405F',
    fields: [
      { key: 'igBusinessAccountId', label: 'IG Business Account ID', placeholder: '1789...' },
      { key: 'accessToken', label: 'Access Token', placeholder: 'IGQVJ...', type: 'password' },
    ],
  },
  x: {
    name: 'X (Twitter)',
    icon: '𝕏',
    color: '#000',
    fields: [
      { key: 'apiKey', label: 'API Key', placeholder: 'xxx' },
      { key: 'apiSecret', label: 'API Secret', placeholder: 'xxx', type: 'password' },
      { key: 'accessToken', label: 'Access Token', placeholder: 'xxx' },
      { key: 'accessSecret', label: 'Access Token Secret', placeholder: 'xxx', type: 'password' },
    ],
  },
  tiktok: {
    name: 'TikTok',
    icon: '♪',
    color: '#010101',
    fields: [
      { key: 'clientKey', label: 'Client Key', placeholder: 'awxxx' },
      { key: 'clientSecret', label: 'Client Secret', placeholder: 'xxx', type: 'password' },
      { key: 'accessToken', label: 'Access Token', placeholder: 'xxx', type: 'password' },
    ],
  },
  youtube: {
    name: 'YouTube',
    icon: '▶',
    color: '#FF0000',
    fields: [
      { key: 'clientId', label: 'OAuth Client ID', placeholder: 'xxx.apps.googleusercontent.com' },
      { key: 'clientSecret', label: 'OAuth Client Secret', placeholder: 'GOCSPX-xxx', type: 'password' },
      { key: 'refreshToken', label: 'Refresh Token', placeholder: '1//xxx', type: 'password' },
    ],
  },
  linkedin: {
    name: 'LinkedIn',
    icon: 'in',
    color: '#0A66C2',
    fields: [
      { key: 'clientId', label: 'Client ID', placeholder: '86xxx' },
      { key: 'clientSecret', label: 'Client Secret', placeholder: 'xxx', type: 'password' },
      { key: 'accessToken', label: 'Access Token', placeholder: 'AQUxxx', type: 'password' },
      { key: 'organizationId', label: 'Organization ID', placeholder: '1234567' },
    ],
  },
  ads: {
    name: 'Ads',
    icon: '📢',
    color: '#F4B400',
    fields: [
      { key: 'apiKey', label: 'API-key', placeholder: 'Plak je Ads API-key', type: 'password' },
      { key: 'adAccountId', label: 'Ad Account ID', placeholder: 'act_123456789' },
      { key: 'provider', label: 'Provider', placeholder: 'meta | google' },
    ],
  },
};

const SocialPlatformSettings = () => {
  const [accounts, setAccounts] = useState<SocialAccount[]>([]);
  const [expanded, setExpanded] = useState<AdPlatform | null>(null);
  const [credentials, setCredentials] = useState<Record<string, Record<string, string>>>({});
  const [saving, setSaving] = useState<AdPlatform | null>(null);
  const [testingPlatform, setTestingPlatform] = useState<AdPlatform | null>(null);
  const [feedback, setFeedback] = useState<Record<string, string | null>>({});
  const [testFeedback, setTestFeedback] = useState<Record<string, { ok: boolean; message: string } | null>>({});
  const loadedRef = useRef(false);

  const loadAccounts = () => {
    void api.socialAccounts().then((accs) => {
      setAccounts(accs);
      const creds: Record<string, Record<string, string>> = {};
      for (const acc of accs) {
        const info = PLATFORM_INFO[acc.platform];
        if (info) {
          const platformCreds: Record<string, string> = {};
          for (const field of info.fields) {
            platformCreds[field.key] = '';
          }
          creds[acc.platform] = platformCreds;
        }
      }
      setCredentials(creds);
    }).catch((reason) => {
      console.error('[social-platform-settings] loadAccounts failed', reason);
    });
  };

  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    loadAccounts();
  }, []);

  const getAccount = (platform: AdPlatform): SocialAccount | undefined =>
    accounts.find((a) => a.platform === platform && a.status === 'active');

  const handleSave = async (platform: AdPlatform) => {
    setSaving(platform);
    setFeedback((prev) => ({ ...prev, [platform]: null }));
    const info = PLATFORM_INFO[platform];
    const creds = credentials[platform] ?? {};
    const hasValues = Object.values(creds).some((v) => v.trim());
    try {
      const existing = getAccount(platform);
      if (existing) {
        await api.disconnectSocialAccount(existing.id);
      }
      if (hasValues) {
        await api.connectSocialAccount({
          platform,
          accountName: info.name,
          accountHandle: platform,
          credentials: creds,
        });
      }
      loadAccounts();
      setFeedback((prev) => ({ ...prev, [platform]: hasValues ? '✅ Opgeslagen' : 'Verbinding verbroken' }));
    } catch (reason) {
      const message = reason instanceof ApiClientError ? reason.message : '❌ Opslaan mislukt';
      setFeedback((prev) => ({ ...prev, [platform]: message }));
      console.error('[social-platform-settings] save failed', platform, reason);
    } finally {
      setSaving(null);
    }
  };

  const handleTest = async (platform: AdPlatform) => {
    const account = getAccount(platform);
    if (!account) {
      setTestFeedback((prev) => ({ ...prev, [platform]: { ok: false, message: 'Geen verbonden account om te testen' } }));
      return;
    }
    setTestingPlatform(platform);
    setTestFeedback((prev) => ({ ...prev, [platform]: null }));
    try {
      const result = await api.testSocialAccount(account.id);
      setTestFeedback((prev) => ({
        ...prev,
        [platform]: { ok: result.success, message: result.message ?? (result.success ? 'Verbinding OK' : 'Test mislukt') },
      }));
    } catch (reason) {
      const message = reason instanceof ApiClientError ? reason.message : 'Test mislukt';
      setTestFeedback((prev) => ({ ...prev, [platform]: { ok: false, message } }));
      console.error('[social-platform-settings] test failed', platform, reason);
    } finally {
      setTestingPlatform(null);
    }
  };

  const handleFieldChange = (platform: AdPlatform, fieldKey: string, value: string) => {
    setCredentials((prev) => ({
      ...prev,
      [platform]: { ...(prev[platform] ?? {}), [fieldKey]: value },
    }));
  };

  return (
    <section className="social-platform-settings">
      <h3>Social Marketing API</h3>
      <p className="muted small" style={{ marginBottom: '1rem' }}>
        Verbind je social media accounts met je eigen API credentials. Per platform vul je de benodigde keys in.
        Deze worden versleuteld opgeslagen op de server.
      </p>

      <div className="platform-settings-list">
        {(Object.entries(PLATFORM_INFO) as [AdPlatform, typeof PLATFORM_INFO[AdPlatform]][]).map(([platform, info]) => {
          const account = getAccount(platform);
          const isOpen = expanded === platform;
          return (
            <div key={platform} className={`platform-setting-card ${isOpen ? 'open' : ''} ${account ? 'connected' : ''}`}>
              <button
                type="button"
                className="platform-setting-header"
                onClick={() => setExpanded(isOpen ? null : platform)}
              >
                <span className="platform-icon" style={{ background: info.color }}>{info.icon}</span>
                <span className="platform-setting-name">{info.name}</span>
                <span className={`platform-connection-status ${account ? 'connected' : 'disconnected'}`}>
                  {account ? '● Verbonden' : '● Niet verbonden'}
                </span>
                <span className="platform-chevron">{isOpen ? '▾' : '▸'}</span>
              </button>

              {isOpen && (
                <div className="platform-setting-body">
                  {info.fields.map((field) => (
                    <label key={field.key} className="platform-credential-field">
                      <span>{field.label}</span>
                      <input
                        type={field.type ?? 'text'}
                        value={credentials[platform]?.[field.key] ?? ''}
                        onChange={(e) => handleFieldChange(platform, field.key, e.target.value)}
                        placeholder={field.placeholder}
                        autoComplete="off"
                      />
                    </label>
                  ))}
                  <div className="platform-setting-actions">
                    <button
                      type="button"
                      className="button primary small"
                      disabled={saving === platform}
                      onClick={() => void handleSave(platform)}
                    >
                      {saving === platform ? 'Opslaan…' : 'Opslaan'}
                    </button>
                    {account && (
                      <button
                        type="button"
                        className="button small"
                        disabled={testingPlatform === platform}
                        onClick={() => void handleTest(platform)}
                        title="Test de verbinding met dit platform"
                      >
                        {testingPlatform === platform ? 'Testen…' : 'Test verbinding'}
                      </button>
                    )}
                    {account && (
                      <button
                        type="button"
                        className="button danger small"
                        onClick={() => void api.disconnectSocialAccount(account.id).then(loadAccounts).catch((reason) => {
                          console.error('[social-platform-settings] disconnect failed', platform, reason);
                        })}
                      >
                        Verbreken
                      </button>
                    )}
                  </div>
                  {feedback[platform] && (
                    <p className={`notice small ${feedback[platform]?.startsWith('✅') ? 'success' : 'error'}`}>
                      {feedback[platform]}
                    </p>
                  )}
                  {testFeedback[platform] && (
                    <p className={`notice small ${testFeedback[platform]?.ok ? 'success' : 'error'}`}>
                      {testFeedback[platform]?.ok ? '✅' : '❌'} {testFeedback[platform]?.message}
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

    </section>
  );
};

export default SocialPlatformSettings;
